import { Component, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { InspectionModel } from '../../../model';
import { ModelState } from '../../../store/model/model.state';

@Component({
  selector: 'app-inspection-model-edit',
  imports: [
    ReactiveFormsModule,
    NzModalModule,
    NzFormModule,
    NzInputNumberModule,
    NzSwitchModule,
    NzButtonModule,
    NzInputModule,
    NzGridModule,
    NzDividerModule,
  ],
  templateUrl: './inspection-model-edit.component.html',
  styleUrl: './inspection-model-edit.component.scss',
})
export class InspectionModelEditComponent {
  readonly model = input<InspectionModel | null>(null);
  readonly saved = output<void>();
  readonly cancelled = output<void>();

  private state = inject(ModelState);
  private fb = inject(FormBuilder);

  saving = signal(false);
  isVisible = signal(false);

  form = this.fb.group({
    isActive: [false as boolean],
    isUsingDeltaE: [false as boolean],
    maxCi4ReliablePrediction: [10 as number],
    deThreshold: [null as number | null],
    dcThreshold: [null as number | null],
    xdeThreshold: [null as number | null],
    dlMaxThreshold: [5 as number],
    dlMinThreshold: [-5 as number],
    daMaxThreshold: [5 as number],
    daMinThreshold: [-5 as number],
    dbMaxThreshold: [5 as number],
    dbMinThreshold: [-5 as number],
    crossDLMaxThreshold: [5 as number],
    crossDLMinThreshold: [-5 as number],
    crossDAMaxThreshold: [5 as number],
    crossDAMinThreshold: [-5 as number],
    crossDBMaxThreshold: [5 as number],
    crossDBMinThreshold: [-5 as number],
    description: [null as string | null],
  });

  constructor() {
    effect(() => {
      const m = this.model();
      if (m) {
        this.form.reset({
          isActive: m.isActive,
          isUsingDeltaE: m.isUsingDeltaE,
          maxCi4ReliablePrediction: m.maxCi4ReliablePrediction ?? 10,
          deThreshold: m.deThreshold ?? null,
          dcThreshold: m.dcThreshold ?? null,
          xdeThreshold: m.xdeThreshold ?? null,
          dlMaxThreshold: m.dlMaxThreshold,
          dlMinThreshold: m.dlMinThreshold,
          daMaxThreshold: m.daMaxThreshold,
          daMinThreshold: m.daMinThreshold,
          dbMaxThreshold: m.dbMaxThreshold,
          dbMinThreshold: m.dbMinThreshold,
          crossDLMaxThreshold: m.crossDLMaxThreshold,
          crossDLMinThreshold: m.crossDLMinThreshold,
          crossDAMaxThreshold: m.crossDAMaxThreshold,
          crossDAMinThreshold: m.crossDAMinThreshold,
          crossDBMaxThreshold: m.crossDBMaxThreshold,
          crossDBMinThreshold: m.crossDBMinThreshold,
          description: m.description ?? null,
        });
        this.isVisible.set(true);
      } else {
        this.isVisible.set(false);
      }
    });
  }

  onSave() {
    const m = this.model();
    if (!m) return;

    this.saving.set(true);
    const updated: InspectionModel = { ...m, ...(this.form.getRawValue() as Partial<InspectionModel>) };

    this.state.updateModel(updated).subscribe({
      next: () => {
        this.saving.set(false);
        this.saved.emit();
      },
      error: () => {
        this.saving.set(false);
      },
    });
  }

  onCancel() {
    this.cancelled.emit();
  }
}
